<template>
  <app-client-layout title="成績">
    <template #header>
      <h2 class="text-xl font-semibold leading-tight text-gray-800">
        成績一覧
      </h2>
    </template>

    <div class="mx-auto flex w-full max-w-7xl flex-col items-stretch justify-center md:flex-row md:items-start">
      <div class="hidden md:block">
        <ExamList
          :examinations="examinations"
          :selected-examination-index="selectedExaminationIndex"
          @select-examination="selectExamination" />
      </div>

      <section
        class="mx-3 mb-5 mt-3 flex-1 overflow-visible rounded-xl bg-white text-gray-700 shadow sm:mx-5 sm:mt-5 md:max-w-4xl"
        aria-labelledby="result-detail-heading">
        <div class="border-b border-gray-100 px-4 py-4 sm:px-6">
          <h3
            id="result-detail-heading"
            class="text-lg font-bold leading-6 text-gray-900">
            成績詳細
          </h3>
        </div>

        <div
          v-if="!selectedExamination"
          class="px-4 py-8 text-center text-sm font-medium text-gray-500">
          表示できる模試がありません。
        </div>

        <div
          v-else-if="!isPurchased(selectedExamination.id)"
          class="space-y-3 bg-gray-50 p-3 sm:p-5">
          <section class="rounded-lg border border-gray-200 bg-white px-4 py-4">
            <p class="text-sm font-semibold text-gray-700">模試名</p>
            <div class="relative mt-2 md:hidden">
              <button
                class="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-900"
                type="button"
                :aria-expanded="isMobileExamDropdownOpen"
                @click="toggleMobileExamDropdown">
                <span class="min-w-0 flex-1 whitespace-normal leading-5">{{ selectedExamination.title }}</span>
                <svg
                  class="h-3 w-3 shrink-0 transform transition-all duration-200"
                  :class="{ 'rotate-180': isMobileExamDropdownOpen }"
                  fill="none"
                  stroke="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 10">
                  <path
                    d="M15 1.2l-7 7-7-7"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
              <div
                v-show="isMobileExamDropdownOpen"
                class="absolute left-0 right-0 z-20 mt-2 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  v-for="{ examination, index } in mobileExaminationOptions"
                  :key="examination.id || index"
                  class="flex min-h-[44px] w-full items-center border-b border-gray-100 px-3 py-2 text-left text-sm font-medium last:border-b-0"
                  :class="selectedExaminationIndex === index
                    ? 'bg-indigo-500 text-white'
                    : 'text-gray-800 hover:bg-indigo-50'"
                  type="button"
                  @click="selectMobileExamination(index)">
                  <span class="min-w-0 flex-1 whitespace-normal leading-5">{{ examination.title }}</span>
                </button>
              </div>
            </div>
            <p class="mt-2 hidden text-sm leading-6 text-gray-900 md:block">
              {{ selectedExamination.title }}
            </p>
          </section>
          <section class="rounded-lg border border-red-100 bg-red-50 px-4 py-4">
            <p class="text-sm font-semibold text-red-700">成績</p>
            <p class="mt-2 text-base font-bold text-red-600">
              模試未購入のため閲覧できません。
            </p>
          </section>
        </div>

        <div
          v-else
          class="space-y-3 bg-gray-50 p-3 sm:space-y-5 sm:p-5">
          <section class="rounded-lg border border-gray-200 bg-white px-4 py-4">
            <p class="text-sm font-semibold text-gray-700">模試名</p>
            <div class="relative mt-2 md:hidden">
              <button
                class="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-900"
                type="button"
                :aria-expanded="isMobileExamDropdownOpen"
                @click="toggleMobileExamDropdown">
                <span class="min-w-0 flex-1 whitespace-normal leading-5">{{ selectedExamination.title }}</span>
                <svg
                  class="h-3 w-3 shrink-0 transform transition-all duration-200"
                  :class="{ 'rotate-180': isMobileExamDropdownOpen }"
                  fill="none"
                  stroke="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 10">
                  <path
                    d="M15 1.2l-7 7-7-7"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
              <div
                v-show="isMobileExamDropdownOpen"
                class="absolute left-0 right-0 z-20 mt-2 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  v-for="{ examination, index } in mobileExaminationOptions"
                  :key="examination.id || index"
                  class="flex min-h-[44px] w-full items-center border-b border-gray-100 px-3 py-2 text-left text-sm font-medium last:border-b-0"
                  :class="selectedExaminationIndex === index
                    ? 'bg-indigo-500 text-white'
                    : 'text-gray-800 hover:bg-indigo-50'"
                  type="button"
                  @click="selectMobileExamination(index)">
                  <span class="min-w-0 flex-1 whitespace-normal leading-5">{{ examination.title }}</span>
                </button>
              </div>
            </div>
            <p class="mt-2 hidden text-sm font-medium leading-6 text-gray-900 sm:text-base md:block">
              {{ selectedExamination.title }}
            </p>
          </section>

          <section
            class="overflow-hidden rounded-xl border border-gray-200 bg-white"
            aria-label="総合結果">
            <div class="grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              <div>
                <div class="px-4 py-4 text-center">
                  <p class="text-xs font-bold text-gray-600">総合点</p>
                  <p class="mt-2 flex items-baseline justify-center gap-1 text-gray-900">
                    <strong class="text-3xl leading-none tracking-normal">{{ currentTotalScore }}</strong>
                    <span class="text-sm font-bold text-gray-600">/ 200</span>
                  </p>
                </div>

                <div class="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
                  <div
                    v-for="(summary, index) in passScoreSummaries"
                    :key="summary.label"
                    class="px-2 py-3 sm:px-4">
                    <p class="text-xs font-bold text-gray-600">{{ summary.label }}</p>
                    <div
                      class="mt-2 flex min-h-[32px] flex-nowrap items-center justify-center gap-[3px] sm:gap-2"
                      :class="index === 0 ? 'sm:translate-x-2' : ''">
                      <p
                        v-if="summary.completed"
                        class="flex items-baseline gap-1 whitespace-nowrap text-gray-900">
                        <strong class="text-2xl leading-none tracking-normal">{{ summary.score }}</strong>
                        <span class="text-xs font-bold text-gray-600">/{{ summary.max }}</span>
                      </p>
                      <p
                        v-else
                        class="text-sm font-bold text-gray-500">
                        未実施
                      </p>
                      <span :class="getPassFailChipClass(summary.result, 'outlineCompact')">
                        {{ summary.result }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex min-h-[104px] flex-col items-center justify-center px-4 py-3 text-center sm:min-h-[132px] sm:py-5">
                <p class="text-xs font-bold text-gray-600">合否</p>
                <span
                  class="mt-2"
                  :class="getPassFailChipClass(overallPassFailResult, 'solid')">
                  {{ overallPassFailResult }}
                </span>
              </div>
            </div>

            <div class="space-y-2 border-t border-gray-200 px-4 py-3">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-bold text-gray-800">
                  全国順位
                </span>
                <template v-if="getRanking(selectedExamination.id)">
                  <span class="text-base font-black text-gray-900">
                    {{ getRanking(selectedExamination.id) }}位
                  </span>
                  <span class="text-xs font-semibold text-gray-500">
                    / {{ getUserNum(selectedExamination.id) }}人中
                  </span>
                  <span class="text-xs font-semibold text-gray-500">
                    ※毎日午前3時に更新されます。
                  </span>
                </template>
                <template v-else-if="selectedExamination.result_announcement_date">
                  <span class="inline-flex h-7 items-center rounded-full bg-gray-200 px-3 text-sm font-bold text-gray-700">
                    未集計
                  </span>
                  <span class="text-xs font-semibold text-gray-500">
                    集計後に表示されます
                  </span>
                </template>
                <template v-else>
                  <span class="inline-flex h-7 items-center rounded-full bg-gray-100 px-3 text-sm font-bold text-gray-500">
                    集計前
                  </span>
                </template>
              </div>

              <div
                v-if="getSchoolRanking(selectedExamination.id)"
                class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-bold text-gray-800">
                  校内順位
                </span>
                <span class="text-base font-black text-gray-900">
                  {{ getSchoolRanking(selectedExamination.id) }}位
                </span>
                <span class="text-xs font-semibold text-gray-500">
                  / {{ getSchoolUserNum(selectedExamination.id) }}人中
                </span>
              </div>
            </div>
          </section>

          <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <section
              class="rounded-xl border border-gray-200 bg-white p-4"
              aria-labelledby="detail-score-title">
              <h4
                id="detail-score-title"
                class="text-sm font-black text-gray-900">
                詳細スコア
              </h4>

              <div class="mt-3 overflow-hidden rounded-lg border border-gray-100">
                <div
                  v-for="row in resultScoreRows"
                  :key="row.label"
                  class="flex min-h-[44px] items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
                  :class="row.isTotal ? 'bg-blue-50 font-bold' : 'bg-white'">
                  <span class="whitespace-nowrap text-sm font-semibold text-gray-800">{{ row.label }}</span>
                  <span class="flex shrink-0 items-center gap-2">
                    <span
                      v-if="row.completed"
                      class="whitespace-nowrap text-sm font-bold text-gray-700">
                      <strong
                        class="text-gray-900"
                        :class="row.isTotal ? 'text-lg sm:text-xl' : 'text-base'">
                        <span v-if="row.isExcluded">(</span>{{ row.score }}<span v-if="row.isExcluded">)</span>
                      </strong>
                      /{{ row.max }}
                    </span>
                    <span
                      v-else
                      class="text-sm font-bold text-gray-500">
                      未実施
                    </span>
                    <span
                      v-if="row.completed"
                      :class="getGradeBadgeClass(row.rank)">
                      {{ row.rank }}
                    </span>
                    <span
                      v-else
                      class="inline-flex h-7 w-8 items-center justify-center rounded-md bg-gray-100 text-sm font-bold text-gray-400">
                      -
                    </span>
                  </span>
                </div>
              </div>

              <div class="mt-3 space-y-1 text-xs font-medium text-red-500">
                <p v-if="isBeforeDate(selectedExamination.result_announcement_date)">
                  ※順位と平均点は後日集計予定となります。
                </p>
                <p v-if="isOveredDate(selectedExamination.result_announcement_date) && isBeforeFiscalYearStart(selectedExamination.reception_start)">
                  ※点数が()付きで表記されている科目につきましては合計点数の計算対象外となります。
                </p>
                <p v-if="isBeforeDate(selectedExamination.result_announcement_date) && !isBeforeFiscalYearStart(selectedExamination.reception_start)">
                  成績集計開始日時：{{ formatDate(selectedExamination.result_announcement_date) }}
                </p>
              </div>
            </section>

            <section
              class="rounded-xl border border-gray-200 bg-white p-4"
              aria-labelledby="field-score-title">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <h4
                  id="field-score-title"
                  class="text-sm font-black text-gray-900">
                  科目別スコア
                </h4>
                <div class="flex flex-col gap-1 text-[11px] font-bold text-gray-500">
                  <span class="inline-flex items-center gap-1"><i class="h-2 w-3 rounded-sm bg-green-500"></i>あなたの正答率（％）</span>
                  <span class="inline-flex items-center gap-1"><i class="h-2 w-3 rounded-sm bg-gray-300"></i>全国平均（％）</span>
                </div>
              </div>
              <div class="mt-2 h-52 overflow-hidden sm:h-56">
                <BarChart
                  css-classes="h-full w-full"
                  :chart-data="getBarData(selectedExamination.id)"
                  :options="scoreBarChartOptions" />
              </div>
            </section>
          </div>

          <section
            v-if="shouldShowSubjectAnalytics"
            class="rounded-xl border border-gray-200 bg-white p-4"
            aria-labelledby="subject-analysis-title">
            <h4
              id="subject-analysis-title"
              class="text-sm font-black text-gray-900">
              分野別分析
            </h4>

            <div class="mt-0 flex flex-col gap-3 md:mt-4 md:flex-row md:items-start md:gap-4 md:pr-4 lg:pr-6">
              <div class="min-w-0 md:w-[69%] md:flex-none">
                <div
                  class="h-60 cursor-pointer sm:h-72 md:h-[360px]"
                  @click="handleSubjectChartClick">
                  <RadarChart
                    ref="subjectChartRef"
                    css-classes="h-full w-full"
                    :chart-data="subjectBreakdownChartData"
                    :options="subjectBreakdownChartOptions" />
                </div>
                <div class="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] font-bold text-gray-500 md:mt-5">
                  <span class="inline-flex items-center gap-1"><i class="h-2 w-4 rounded-sm border-2 border-green-600"></i>あなたの正答率（％）</span>
                  <span
                    v-if="isNationalAnalyticsReady"
                    class="inline-flex items-center gap-1"><i class="h-2 w-4 rounded-sm border-2 border-gray-400"></i>全国平均（％）</span>
                </div>
              </div>

              <div class="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-[31%] md:flex-none md:grid-cols-1">
                <button
                  v-for="subject in currentSubjectOptions"
                  :key="subject.id"
                  type="button"
                  class="flex min-h-[64px] items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition"
                  :class="selectedSubjectId === subject.id
                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:text-blue-700'"
                  @click="openSubjectModal(subject.id)">
                  <span class="min-w-0 flex-1">
                    <strong class="block truncate text-sm font-bold leading-snug text-gray-900">
                      {{ subject.label }}
                    </strong>
                    <small class="mt-1 block whitespace-nowrap text-xs font-semibold text-gray-500">
                      <b class="text-green-700">あなた {{ subject.personalRate }}%</b>
                      <template v-if="isNationalAnalyticsReady">
                        <span class="mx-1">/</span>
                        全国 {{ subject.nationalRate }}%
                      </template>
                    </small>
                  </span>
                  <span class="flex h-8 w-5 shrink-0 items-center justify-center text-gray-400">
                    <svg
                      class="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </section>

          <section
            v-else-if="shouldShowSubjectAnalyticsMissingState"
            class="rounded-xl border border-gray-200 bg-white p-4"
            aria-labelledby="subject-analysis-title">
            <h4
              id="subject-analysis-title"
              class="text-sm font-black text-gray-900">
              分野別分析
            </h4>
            <div class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              回答明細がないため、分野別分析を表示できません。
            </div>
          </section>

          <QuestionCorrectAnswerRate
            v-if="currentQuestionAnswerAnalytics.questions.length"
            mode="student"
            :items="currentQuestionAnswerAnalytics.questions"
            :examination-types="examinationTypes"
            :subject-names="subjectNames"
            :national-question-rates-ready="currentQuestionAnswerAnalytics.nationalQuestionRatesReady" />

          <section
            class="rounded-xl border border-gray-200 bg-white p-4"
            aria-labelledby="commentary-actions-title">
            <h4
              id="commentary-actions-title"
              class="text-sm font-black text-gray-900">
              解説・解答
            </h4>

            <div
              v-if="isFinishAllType(selectedExamination.id)"
              class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                class="flex min-h-[46px] items-center rounded-lg bg-green-600 px-4 text-sm font-bold text-white shadow-sm transition lg:hover:bg-green-500 active:bg-green-500"
                type="button"
                @click="onClickCommentary(selectedExamination.id, 1)">
                <span class="flex-1 text-center">解説ページへ</span>
                <span class="flex h-8 w-5 shrink-0 items-center justify-center">
                  <svg
                    class="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
              <a
                class="flex min-h-[46px] items-center rounded-lg bg-yellow-500 px-4 text-sm font-bold text-white shadow-sm transition lg:hover:bg-yellow-400 active:bg-yellow-400"
                :href="route('commentary.answer-list', { examination: selectedExamination.id })"
                target="_blank">
                <span class="flex-1 text-center">解答一覧</span>
                <span class="flex h-8 w-5 shrink-0 items-center justify-center">
                  <svg
                    class="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </a>
            </div>
            <div
              v-else
              class="mt-3 rounded-lg bg-gray-100 px-4 py-3 text-sm font-bold text-gray-500">
              未実施の種別があります
            </div>

            <div
              v-if="selectedExaminationMovieUrls.length"
              class="mt-4 border-t border-gray-100 pt-4">
              <h5 class="text-sm font-bold text-gray-900">解説動画</h5>
              <div class="mt-2 space-y-2">
                <a
                  v-for="(movieUrl, index) in selectedExaminationMovieUrls"
                  :key="index"
                  class="flex min-h-[40px] items-center rounded-lg border border-gray-200 px-3 text-sm font-semibold text-blue-700"
                  :href="movieUrl.url"
                  target="_blank">
                  {{ movieUrl.title }}
                </a>
              </div>
            </div>
          </section>

          <section
            class="self-start rounded-xl border border-gray-200 bg-white p-4 pb-2"
            aria-labelledby="download-title">
            <h4
              id="download-title"
              class="text-sm font-black text-gray-900">
              ダウンロード
            </h4>

            <div
              v-if="isFinishAllType(selectedExamination.id)"
              class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div class="overflow-hidden rounded-lg border border-gray-200">
                <div class="bg-gray-50 px-3 py-2 text-xs font-bold text-gray-600">問題PDF</div>
                <a
                  v-for="download in questionPdfLinks"
                  :key="download.label"
                  class="flex min-h-[44px] items-center gap-2 border-t border-gray-100 px-3 text-sm font-semibold text-gray-800"
                  :href="download.href">
                  <span class="inline-flex h-6 w-6 items-center justify-center rounded bg-blue-500 text-[10px] font-black text-white">P</span>
                  <span class="min-w-0 flex-1 truncate">{{ download.label }}</span>
                  <span class="flex h-8 w-5 shrink-0 items-center justify-center text-gray-400">
                    <svg
                      class="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </a>
              </div>

              <div class="overflow-hidden rounded-lg border border-gray-200">
                <div class="bg-gray-50 px-3 py-2 text-xs font-bold text-gray-600">解答PDF</div>
                <a
                  v-for="download in answerPdfLinks"
                  :key="download.label"
                  class="flex min-h-[44px] items-center gap-2 border-t border-gray-100 px-3 text-sm font-semibold text-gray-800"
                  :href="download.href">
                  <span class="inline-flex h-6 w-6 items-center justify-center rounded bg-green-500 text-[10px] font-black text-white">A</span>
                  <span class="min-w-0 flex-1 truncate">{{ download.label }}</span>
                  <span class="flex h-8 w-5 shrink-0 items-center justify-center text-gray-400">
                    <svg
                      class="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </a>
              </div>

              <div class="self-start overflow-hidden rounded-lg border border-purple-100 sm:col-span-2 lg:col-span-1">
                <div class="bg-purple-50 px-3 py-2 text-xs font-bold text-purple-700">まとめてダウンロード</div>
                <a
                  class="flex min-h-[44px] items-center gap-2 border-t border-purple-100 px-3 py-2 text-sm font-bold text-gray-900"
                  :href="route('result.downloadZip', {
                    examination_id: selectedExamination.id,
                  })">
                  <span class="inline-flex h-6 w-6 items-center justify-center rounded bg-purple-500 text-[10px] font-black text-white">Z</span>
                  <span class="min-w-0 flex-1 truncate">ZIPをダウンロード</span>
                  <span class="flex h-8 w-5 shrink-0 items-center justify-center text-gray-400">
                    <svg
                      class="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </a>
              </div>
            </div>

            <div
              v-else
              class="mt-3 whitespace-pre-line rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
              未実施の種別があります。<br />
              問題と解説のPDFは全模試種別実施後にダウンロード可能となります。
            </div>
          </section>
        </div>
      </section>
    </div>

    <DialogModal
      :show="isSubjectModalOpen"
      max-width="2xl"
      show-scroll-hint
      @close="closeSubjectModal">
      <template #title>
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-semibold text-gray-900 sm:text-lg">
              {{ selectedSubjectLabel }} の分野別成績
            </div>
            <div class="mt-1 text-[11px] leading-4 text-gray-500 sm:text-sm">
              {{ isNationalAnalyticsReady ? 'あなたの正答率と全国正答率を分野ごとに比較できます。' : 'あなたの正答率を分野ごとに確認できます。' }}
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <div
          v-if="selectedSubjectId"
          class="space-y-5 sm:space-y-6">
          <!-- 4項目は2列、3項目は横並びにして分野別サマリーの高さを揃える -->
          <div
            class="grid gap-2 sm:gap-4"
            :class="isNationalAnalyticsReady ? 'grid-cols-2' : 'grid-cols-3'">
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-gray-700 sm:text-xs">あなたの正答率</div>
              <div class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{{ selectedSubjectPersonalRate }}%</div>
            </div>
            <div
              v-if="isNationalAnalyticsReady"
              class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-gray-200 bg-gray-100 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-gray-600 sm:text-xs">全国平均</div>
              <div class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{{ selectedSubjectNationalRate }}%</div>
            </div>
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-green-100 bg-green-50 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-green-600 sm:text-xs">強い分野</div>
              <div class="space-y-1">
                <div class="break-words text-sm font-semibold leading-5 text-green-900 sm:text-base">
                  {{ strongestField?.name || '-' }}
                </div>
                <div class="text-2xl font-bold tracking-tight text-green-900 sm:text-3xl">
                  {{ strongestField?.personalRate ?? '-' }}%
                </div>
              </div>
            </div>
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-red-100 bg-red-50 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-red-600 sm:text-xs">弱い分野</div>
              <div class="space-y-1">
                <div class="break-words text-sm font-semibold leading-5 text-red-900 sm:text-base">
                  {{ weakestField?.name || '-' }}
                </div>
                <div class="text-2xl font-bold tracking-tight text-red-900 sm:text-3xl">
                  {{ weakestField?.personalRate ?? '-' }}%
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-gray-100 bg-gray-50 p-3 sm:p-4">
            <div class="h-64 sm:h-80">
              <BarChart
                :key="selectedSubjectId"
                :chart-data="selectedFieldChartData"
                :css-classes="'h-full w-full'"
                :options="fieldBreakdownChartOptions"
                :styles="fieldBreakdownChartStyles" />
            </div>
          </div>

          <AppDownloadCta
            v-if="shouldShowAppDownloadCta"
            :weak-field="weakestField"
            :examination-id="currentExaminationId"
            :subject-id="selectedSubject?.id"
            :enabled="appPracticeEnabled" />

          <div class="space-y-3 md:hidden">
            <div
              v-for="field in selectedFieldBreakdown"
              :key="field.name"
              class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-semibold leading-6 text-gray-900">
                    {{ field.name }}
                  </div>
                  <div class="mt-1 text-sm font-medium text-gray-700">
                    {{ formatFieldAnswerSummary(field) }}
                  </div>
                </div>
                <div class="shrink-0 text-right text-xs font-medium text-gray-500">
                  分野別
                </div>
              </div>

              <div class="mt-4 space-y-2">
                <div class="flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2">
                  <span class="text-xs font-medium text-blue-600">あなた</span>
                  <span class="text-sm font-semibold text-blue-700">{{ field.personalRate }}%</span>
                </div>
                <div
                  v-if="isNationalAnalyticsReady"
                  class="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <span class="text-xs font-medium text-gray-500">全国</span>
                  <span class="text-sm font-semibold text-gray-700">{{ field.nationalRate }}%</span>
                </div>
                <div
                  v-if="isNationalAnalyticsReady"
                  class="flex items-center justify-between rounded-lg bg-white px-3 py-2 ring-1 ring-inset ring-gray-200">
                  <span class="text-xs font-medium text-gray-500">差分</span>
                  <span
                    class="text-sm font-semibold"
                    :class="getGapClass(field.personalRate - field.nationalRate)">
                    {{ formatGap(field.personalRate - field.nationalRate) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="hidden overflow-x-auto rounded-2xl border border-gray-200 md:block">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    分野
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    正答数
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    あなた
                  </th>
                  <th
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    全国
                  </th>
                  <th
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    差分
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr
                  v-for="field in selectedFieldBreakdown"
                  :key="field.name">
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">
                    {{ field.name }}
                  </td>
                  <td class="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-700">
                    {{ formatFieldAnswerSummary(field) }}
                  </td>
                  <td class="px-4 py-3 text-right text-sm text-blue-700">
                    {{ field.personalRate }}%
                  </td>
                  <td
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-sm text-gray-700">
                    {{ field.nationalRate }}%
                  </td>
                  <td
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-sm font-medium"
                    :class="getGapClass(field.personalRate - field.nationalRate)">
                    {{ formatGap(field.personalRate - field.nationalRate) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-700 sm:w-auto"
          @click="closeSubjectModal">
          閉じる
        </button>
      </template>
    </DialogModal>
  </app-client-layout>
</template>

<script setup>
import moment from 'moment'
import { ref, computed } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { Chart, registerables } from 'chart.js'
import { BarChart, RadarChart } from 'vue-chart-3'
import ExamList from '@/Pages/Client/Exam/ExamList'
import DialogModal from '@/Common/DialogModal'
import formatDate from '@/actions/format/date'
import AppClientLayout from '@/Layouts/AppClientLayout'
import { EXAMINATION_TYPES } from '@/Constants/examinationTypes'
import QuestionCorrectAnswerRate from '@/Pages/Client/Partials/QuestionCorrectAnswerRate'
import AppDownloadCta from './Partials/AppDownloadCta'

Chart.register(...registerables)

const props = defineProps({
  appPracticeEnabled: { type: Boolean, default: false },
  examinations: {
    type: Array,
    default: () => [],
  },
  examinationMovieUrls: {
    type: Array,
    default: () => [],
  },
  progresses: {
    type: Array,
    default: () => [],
  },
  purchases: {
    type: Array,
    default: () => [],
  },
  ranking: {
    type: Object,
    default: () => { },
  },
  nationalAggregations: {
    type: Object,
    default: () => ({}),
  },
  passFailResults: {
    type: Object,
    default: () => ({}),
  },
  subjectAnalytics: {
    type: Object,
    default: () => ({}),
  },
  questionAnswerAnalytics: {
    type: Object,
    default: () => ({}),
  },
  examinationTypes: {
    type: Object,
    default: () => ({}),
  },
  selectedExaminationId: {
    type: Number,
    default: null,
  },
  schoolRanking: {
    type: Object,
    default: () => null,
  },
  schoolStudentCounts: {
    type: Object,
    default: () => ({}),
  },
  isAnsweredQuestionnaire: {
    type: Boolean,
    default: false,
  },
  fiscalYearStart: {
    type: String,
    required: true,
  },
})

function resolveDefaultExaminationIndex() {
  const selectedIndex = props.examinations.findIndex((examination) => examination.id === props.selectedExaminationId)

  if (selectedIndex >= 0) {
    return selectedIndex
  }

  if (!props.examinations.length) {
    return 0
  }

  const withinPeriodIndex = props.examinations.findIndex((examination) => {
    const startDate = examination.reception_start || examination.start_date
    const endDate = examination.reception_end || examination.end_date

    return examination.is_published
      && (!startDate || moment().isSameOrAfter(startDate))
      && (!endDate || moment().isSameOrBefore(endDate))
  })

  if (withinPeriodIndex >= 0) {
    return withinPeriodIndex
  }

  return props.examinations.reduce((latestIndex, examination, index) => {
    const latestExamination = props.examinations[latestIndex]
    const examinationDate = moment(examination.start_date || examination.reception_start || 0)
    const latestDate = moment(latestExamination.start_date || latestExamination.reception_start || 0)

    if (examinationDate.isAfter(latestDate)) {
      return index
    }

    return latestIndex
  }, 0)
}

const selectedExaminationIndex = ref(resolveDefaultExaminationIndex())
const subjectChartRef = ref(null)
const selectedSubjectId = ref(null)
const isMobileExamDropdownOpen = ref(false)

const scoreRowDefinitions = [
  { label: '必須', type: 1, max: 50 },
  { label: '一般', type: 2, max: 100 },
  { label: '実地', type: 3, max: 50 },
]

const mobileExaminationOptions = computed(() => props.examinations
  .map((examination, index) => ({ examination, index }))
  .sort((a, b) => {
    const aDate = moment(a.examination.start_date || a.examination.reception_start || 0)
    const bDate = moment(b.examination.start_date || b.examination.reception_start || 0)

    return bDate.diff(aDate)
  }))

const selectExamination = (index) => {
  selectedSubjectId.value = null
  const examination = props.examinations[index]

  if (!examination) {
    return
  }

  Inertia.get(route('result'), {
    examination_id: examination.id,
  }, {
    preserveScroll: true,
  })
}

const toggleMobileExamDropdown = () => {
  isMobileExamDropdownOpen.value = !isMobileExamDropdownOpen.value
}

const selectMobileExamination = (index) => {
  isMobileExamDropdownOpen.value = false
  selectExamination(index)
}

const selectedExamination = computed(() => props.examinations[selectedExaminationIndex.value] || null)

const isSubjectModalOpen = computed(() => !!selectedSubjectId.value)

const currentExaminationId = computed(() => selectedExamination.value?.id)

const currentSubjectAnalytics = computed(() => {
  if (!currentExaminationId.value) {
    return null
  }

  return props.subjectAnalytics[currentExaminationId.value] || null
})

const currentQuestionAnswerAnalytics = computed(() => {
  if (!currentExaminationId.value) {
    return {
      nationalQuestionRatesReady: false,
      questions: [],
    }
  }

  return props.questionAnswerAnalytics[currentExaminationId.value] || {
    nationalQuestionRatesReady: false,
    questions: [],
  }
})

const subjectNames = [
  { id: 1, name: '基礎動物学' },
  { id: 2, name: '基礎動物看護学' },
  { id: 3, name: '臨床動物看護学' },
  { id: 4, name: '愛護・適正飼養学' },
  { id: 5, name: '実習' },
]

const isNationalAnalyticsReady = computed(() => currentSubjectAnalytics.value?.nationalAnalyticsReady === true)

const hasPersonalSubjectAnalytics = computed(() => currentSubjectAnalytics.value?.hasPersonalAnswerData === true)

const currentSubjectOptions = computed(() => currentSubjectAnalytics.value?.subjects || [])

const currentSubjectFieldBreakdowns = computed(() => currentSubjectAnalytics.value?.fieldBreakdowns || {})

const hasSubjectAnalyticsDefinition = computed(() => currentSubjectOptions.value.length > 0)

const selectedFieldBreakdown = computed(() => currentSubjectFieldBreakdowns.value[selectedSubjectId.value] || [])

const selectedSubject = computed(() => currentSubjectOptions.value.find((subject) => subject.id === selectedSubjectId.value))

const selectedSubjectLabel = computed(() => {
  if (!selectedSubject.value) {
    return ''
  }

  return selectedSubject.value.label
})

const getShortSubjectLabel = (label) => ({
  基礎動物学: '基礎',
  基礎動物看護学: '基礎看護',
  臨床動物看護学: '臨床看護',
  '愛護・適正飼養学': '愛護',
  実習: '実習',
}[label] || label)

const openSubjectModal = (subjectId) => {
  selectedSubjectId.value = subjectId
}

const subjectBreakdownChartData = computed(() => {
  const datasets = [
    {
      label: 'あなたの正答率(%)',
      data: currentSubjectOptions.value.map((subject) => subject.personalRate),
      backgroundColor: 'rgba(9, 169, 99, 0.14)',
      borderColor: '#09A963',
      pointBackgroundColor: '#09A963',
      pointBorderColor: '#FFFFFF',
      pointHoverBackgroundColor: '#09A963',
      pointHoverBorderColor: '#FFFFFF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    },
  ]

  if (isNationalAnalyticsReady.value) {
    datasets.push({
      label: '全国平均(%)',
      data: currentSubjectOptions.value.map((subject) => subject.nationalRate),
      backgroundColor: 'rgba(107, 114, 128, 0.14)',
      borderColor: '#9CA3AF',
      pointBackgroundColor: '#9CA3AF',
      pointBorderColor: '#FFFFFF',
      pointHoverBackgroundColor: '#9CA3AF',
      pointHoverBorderColor: '#FFFFFF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    })
  }

  return {
    labels: currentSubjectOptions.value.map((subject) => getShortSubjectLabel(subject.label)),
    datasets,
  }
})

const subjectBreakdownChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'nearest',
    intersect: false,
  },
  onClick: (_event, elements) => {
    if (!elements.length) {
      return
    }

    const subject = currentSubjectOptions.value[elements[0].index]

    if (subject) {
      openSubjectModal(subject.id)
    }
  },
  onHover: (_event, elements, chart) => {
    const { canvas } = chart

    canvas.style.cursor = elements.length ? 'pointer' : 'default'
  },
  plugins: {
    legend: {
      display: false,
    },
  },
  elements: {
    point: {
      hitRadius: 18,
    },
  },
  scales: {
    r: {
      beginAtZero: true,
      min: 0,
      max: 100,
      ticks: {
        stepSize: 20,
        callback: (value) => `${value}%`,
        backdropColor: 'transparent',
      },
      pointLabels: {
        font: {
          size: 10,
        },
      },
    },
  },
}))

const scoreBarChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  layout: {
    padding: {
      bottom: 0,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        stepSize: 10,
        callback: (value) => `${value}%`,
      },
    },
  },
}

const fieldBreakdownChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 700,
    easing: 'easeOutQuart',
  },
  animations: {
    x: {
      duration: 0,
    },
    base: {
      from: (context) => context.chart.scales.y.getBasePixel(),
    },
    y: {
      from: (context) => context.chart.scales.y.getBasePixel(),
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        callback: (value) => `${value}%`,
      },
    },
  },
}

const fieldBreakdownChartStyles = {
  height: '100%',
  width: '100%',
}

const selectedSubjectPersonalRate = computed(() => {
  if (!selectedSubject.value) {
    return 0
  }

  return selectedSubject.value.personalRate
})

const selectedSubjectNationalRate = computed(() => {
  if (!selectedSubject.value || !isNationalAnalyticsReady.value) {
    return null
  }

  return selectedSubject.value.nationalRate
})

const strongestField = computed(() => {
  if (!selectedFieldBreakdown.value.length) {
    return null
  }

  return [...selectedFieldBreakdown.value].sort((left, right) => right.personalRate - left.personalRate)[0]
})

const weakestField = computed(() => {
  if (!selectedFieldBreakdown.value.length) {
    return null
  }

  // 本人の正答率が最も低い分野を「弱い分野」として表示する。
  return [...selectedFieldBreakdown.value].sort((left, right) => (
    left.personalRate - right.personalRate
    || right.questionCount - left.questionCount
    || (left.fieldId ?? Number.MAX_SAFE_INTEGER) - (right.fieldId ?? Number.MAX_SAFE_INTEGER)
    || (left.name < right.name ? -1 : Number(left.name > right.name))
  ))[0]
})

const selectedFieldChartData = computed(() => {
  const datasets = [
    {
      label: 'あなたの正答率',
      data: selectedFieldBreakdown.value.map((field) => field.personalRate),
      backgroundColor: '#2ECC71',
      borderRadius: 8,
    },
  ]

  if (isNationalAnalyticsReady.value) {
    datasets.push({
      label: '全国正答率',
      data: selectedFieldBreakdown.value.map((field) => field.nationalRate),
      backgroundColor: '#9AE6B4',
      borderRadius: 8,
    })
  }

  return {
    labels: selectedFieldBreakdown.value.map((field) => field.name),
    datasets,
  }
})

const getFieldQuestionCount = (field) => field.questionCount || 8

const getFieldCorrectCount = (field) => field.correctCount
  ?? Math.round((field.personalRate / 100) * getFieldQuestionCount(field))

const formatFieldAnswerSummary = (field) => `${getFieldCorrectCount(field)}問 / ${getFieldQuestionCount(field)}問`

const closeSubjectModal = () => {
  selectedSubjectId.value = null
}

const handleSubjectChartClick = (event) => {
  const chart = subjectChartRef.value?.chartInstance

  if (!chart) {
    return
  }

  const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true)

  if (!elements.length) {
    return
  }

  const subject = currentSubjectOptions.value[elements[0].index]

  if (subject) {
    openSubjectModal(subject.id)
  }
}

const formatGap = (gap) => `${gap > 0 ? '+' : ''}${gap}%`

const getGapClass = (gap) => {
  if (gap > 0) {
    return 'text-emerald-600'
  }

  if (gap < 0) {
    return 'text-rose-600'
  }

  return 'text-gray-500'
}

const selectedExaminationMovieUrls = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  return props.examinationMovieUrls.filter((e) => e.examination_id === currentExaminationId.value)
})

const isPurchased = (id) => props.purchases.filter(purchase => (purchase.exam_id === id)).length

const isFinishAllType = (id) => {
  const progresses = props.progresses.filter(progress => (progress.examination_id === id))

  if (progresses.length < 3) {
    return false
  }

  const isFinishAll = progresses.every(element => element.finished_at !== null)
  return isFinishAll
}

const isFinishSelectType = (id, selectType) => !!props.progresses.filter(progress => (progress.examination_id === id && progress.examination_type === selectType)).length

const isFinishExam = (id) => props.progresses.some(progress => (progress.examination_id === id))

const getScore = (id, type) => {
  const value = props.progresses.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  let score = 0
  if (value && value.score !== null) {
    score = value.score
  } else {
    score = 0
  }

  return score
}

const getScorePercentage = (id, type) => {
  const value = props.progresses.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  let score = 0
  if (value && value.score !== null) {
    if (type === 1 || type === 3) {
      return value.score * 2
    }
    return value.score
  }
  score = 0

  return score
}

// TODO 現在集計日時はconfig/services.phpに設定しているが、動的に変更
const isBeforeFiscalYearStart = (date) => {
  const fiscalYearStart = moment(props.fiscalYearStart)
  const momentDate = moment(date)
  return momentDate.isBefore(fiscalYearStart)
}

const getProgressTotalScore = (id) => {
  let totalScore = 0

  for (let i = 1; i <= 3; i += 1) {
    totalScore += Number(getScore(id, i))
  }

  return totalScore
}

const getTotalScore = (id) => {
  const progressTotalScore = getProgressTotalScore(id)
  const examination = props.examinations.find((value) => (value.id === id))

  if (!examination || !isBeforeFiscalYearStart(examination.reception_start)) {
    return progressTotalScore
  }

  const ranking = props.ranking.find((value) => (value.examination_id === id))

  return ranking == null ? progressTotalScore : ranking.score
}

const isCurrentExaminationFinished = computed(() => (
  currentExaminationId.value ? isFinishAllType(currentExaminationId.value) : false
))

// 購入済みかつ全種別を受験完了した場合だけ、アプリ案内CTAを表示する。
const shouldShowAppDownloadCta = computed(() => (
  !!currentExaminationId.value
    && !!isPurchased(currentExaminationId.value)
    && isCurrentExaminationFinished.value
))

const currentTotalScore = computed(() => (
  currentExaminationId.value ? getTotalScore(currentExaminationId.value) : 0
))

const shouldShowSubjectAnalyticsMissingState = computed(() => (
  hasSubjectAnalyticsDefinition.value
    && isCurrentExaminationFinished.value
    && !hasPersonalSubjectAnalytics.value
    && currentTotalScore.value > 0
))

const shouldShowSubjectAnalytics = computed(() => (
  hasSubjectAnalyticsDefinition.value
    && isCurrentExaminationFinished.value
    && !shouldShowSubjectAnalyticsMissingState.value
))

const getNationalAggregation = (id) => props.nationalAggregations[id] || null

const getPassFailResults = (id) => props.passFailResults[id] || []

const getAverageScorePercentage = (id, type) => {
  const aggregation = getNationalAggregation(id)

  if (aggregation && aggregation.average_score_percentages) {
    return aggregation.average_score_percentages[type] || 0
  }

  return 0
}

const calcRank = (val, maxVal) => {
  const percentage = (val * 100) / maxVal

  let rank = ''
  if (percentage >= 80) {
    rank = 'A'
  } else if (percentage >= 60) {
    rank = 'B'
  } else if (percentage >= 40) {
    rank = 'C'
  } else {
    rank = 'D'
  }

  return rank
}

const getRank = (id, type) => {
  const prog = props.progresses.filter(progress => (progress.examination_id === id)
    && (progress.examination_type === type)).pop()

  let intScore = 0
  if (prog && prog.score) {
    intScore = parseInt(prog.score, 10)
    if (type === 1 || type === 3) {
      intScore *= 2
    }
  } else {
    intScore = 0
  }

  return calcRank(intScore, 100)
}

const getGradeBadgeClass = (rank) => {
  const baseClass = 'inline-flex h-7 w-8 items-center justify-center justify-self-end rounded-md text-sm font-black text-white'

  if (rank === 'A') {
    return `${baseClass} bg-yellow-500`
  }

  if (rank === 'B') {
    return `${baseClass} bg-green-500`
  }

  if (rank === 'C') {
    return `${baseClass} bg-yellow-300 text-gray-900`
  }

  return `${baseClass} bg-blue-500`
}

const getPassFailChipClass = (result, variant = 'solid') => {
  const baseClass = 'inline-flex items-center justify-center rounded-full font-black tracking-normal'
  let sizeClass = 'min-h-[28px] min-w-[62px] px-3 text-xs'

  if (variant === 'solid') {
    sizeClass = 'min-h-[32px] min-w-[74px] px-4 text-lg'
  } else if (variant === 'outlineCompact') {
    sizeClass = 'min-h-[24px] min-w-[48px] px-2 text-[11px]'
  }

  if (result === '合格') {
    return `${baseClass} ${sizeClass} ${variant === 'solid'
      ? 'bg-green-600 text-white shadow-sm'
      : 'border border-green-500 bg-white text-green-700'}`
  }

  if (result === '不合格') {
    return `${baseClass} ${sizeClass} ${variant === 'solid'
      ? 'bg-red-600 text-white shadow-sm'
      : 'border border-red-500 bg-white text-red-600'}`
  }

  return `${baseClass} ${sizeClass} ${variant === 'solid'
    ? 'bg-gray-200 text-gray-700'
    : 'border border-gray-300 bg-white text-gray-500'}`
}

const getUserNum = (id) => {
  const aggregation = getNationalAggregation(id)

  if (aggregation && aggregation.user_num) {
    return aggregation.user_num
  }

  return 0
}

const getRanking = (id) => {
  const aggregation = getNationalAggregation(id)

  if (aggregation && aggregation.ranking) {
    return aggregation.ranking
  }

  const ranking = props.ranking.find((value) => (value.examination_id === id))
  if (ranking == null) {
    return false
  }
  return ranking.ranking
}

const getSchoolRanking = (id) => {
  if (!props.schoolRanking || !props.schoolRanking[id]) {
    return false
  }
  return props.schoolRanking[id].ranking
}

const getSchoolUserNum = (id) => {
  if (!props.schoolStudentCounts || !props.schoolStudentCounts[id]) {
    return 0
  }
  return props.schoolStudentCounts[id]
}

const onClickCommentary = (id) => {
  Inertia.get(route('commentary', {
    examination: id,
    type: 1,
  }))
}

const getBarData = (id) => {
  const barData = {
    labels: ['必須', '一般', '実地'],
    datasets: [
      {
        type: 'bar',
        label: 'あなたの正答率(%)',
        data: [getScorePercentage(id, 1), getScorePercentage(id, 2), getScorePercentage(id, 3)],
        fill: true,
        order: 0,
        backgroundColor: '#09A963',
        borderRadius: 6,
      },
      {
        type: 'bar',
        label: '全国平均(%)',
        data: [getAverageScorePercentage(id, 1), getAverageScorePercentage(id, 2), getAverageScorePercentage(id, 3)],
        backgroundColor: '#D8DADD',
        borderRadius: 6,
        fill: true,
        order: 1,
      },
    ],
  }

  return barData
}

const isBeforeDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isBefore(momentDate)
}

const isOveredDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isAfter(momentDate)
}

const isOveredAggregationPeriod = (examination, type) => {
  const prog = props.progresses.filter(progress => (progress.examination_id === examination.id)
    && (progress.examination_type === type)).pop()

  if (!prog) {
    return false
  }

  const progStartAt = moment(prog.started_at)
  const resultAnnouncementDate = moment(examination.result_announcement_date)

  return progStartAt.isAfter(resultAnnouncementDate)
}

const onDownloadPdf = (examinationId, examinationType, pdfType) => (
  route('result.downloadPdf', {
    examination_id: examinationId,
    examination_type: examinationType,
    pdf_type: pdfType,
  })
)

const currentPassFailResults = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  return getPassFailResults(currentExaminationId.value)
})

const getCurrentPassFailResult = (label) => currentPassFailResults.value
  .find((result) => result.label === label)

const overallPassFailResult = computed(() => {
  if (!currentExaminationId.value || !isFinishAllType(currentExaminationId.value)) {
    return '未判定'
  }

  return getCurrentPassFailResult('結果')?.result || '未判定'
})

const passScoreSummaries = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  const id = currentExaminationId.value
  const isCompulsoryCompleted = isFinishSelectType(id, 1)
  const isGeneralCompleted = isFinishSelectType(id, 2)
  const isPracticeCompleted = isFinishSelectType(id, 3)

  return [
    {
      label: '必須',
      score: getScore(id, 1),
      max: 50,
      completed: isCompulsoryCompleted,
      result: isCompulsoryCompleted ? (getCurrentPassFailResult('必須')?.result || '未判定') : '未実施',
    },
    {
      label: '一般＋実地',
      score: getScore(id, 2) + getScore(id, 3),
      max: 150,
      completed: isGeneralCompleted && isPracticeCompleted,
      result: isGeneralCompleted && isPracticeCompleted
        ? (getCurrentPassFailResult('一般＋実地')?.result || '未判定')
        : '未実施',
    },
  ]
})

const resultScoreRows = computed(() => {
  if (!selectedExamination.value || !currentExaminationId.value) {
    return []
  }

  const id = currentExaminationId.value
  const rows = scoreRowDefinitions.map((definition) => {
    const completed = isFinishSelectType(id, definition.type)

    return {
      ...definition,
      completed,
      score: getScore(id, definition.type),
      rank: getRank(id, definition.type),
      isExcluded: completed
        && isOveredAggregationPeriod(selectedExamination.value, definition.type)
        && getRanking(id)
        && isBeforeFiscalYearStart(selectedExamination.value.reception_start),
      isTotal: false,
    }
  })

  const totalScore = currentTotalScore.value

  rows.push({
    label: '合計',
    max: 200,
    completed: isFinishExam(id),
    score: totalScore,
    rank: calcRank(totalScore, 200),
    isExcluded: false,
    isTotal: true,
  })

  return rows
})

const pdfDownloadTypes = computed(() => {
  const types = [
    { label: '一般', value: EXAMINATION_TYPES.general.label },
    { label: '必須', value: EXAMINATION_TYPES.compulsory.label },
  ]

  if (selectedExamination.value?.index >= 7) {
    types.push({ label: '実地', value: EXAMINATION_TYPES.practice.label })
  }

  return types
})

const questionPdfLinks = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  return pdfDownloadTypes.value.map((type) => ({
    label: `${type.label}の問題`,
    href: onDownloadPdf(currentExaminationId.value, type.value, '問題'),
  }))
})

const answerPdfLinks = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  return pdfDownloadTypes.value.map((type) => ({
    label: `${type.label}の解答`,
    href: onDownloadPdf(currentExaminationId.value, type.value, '解答'),
  }))
})

</script>
